import React, { useEffect } from "react";
import {
  MapContainer,
  TileLayer,
  Polyline,
  Marker,
  Popup,
  useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import "./mapaRutaModal.css";
import Button from "../../Button/Button";

// ----------------------------------------
// Función para segmentar la ruta
// ----------------------------------------
function splitRouteByDistance(points, maxDistanceMeters = 200) {
  const segments = [];
  let currentSegment = [];

  const haversineDistance = (p1, p2) => {
    const R = 6371e3;
    const toRad = (v) => (v * Math.PI) / 180;
    const dLat = toRad(p2.lat - p1.lat);
    const dLng = toRad(p2.lng - p1.lng);
    const lat1 = toRad(p1.lat);
    const lat2 = toRad(p2.lat);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  for (let i = 0; i < points.length; i++) {
    if (i === 0) {
      currentSegment.push(points[i]);
    } else {
      const dist = haversineDistance(points[i - 1], points[i]);
      if (dist > maxDistanceMeters) {
        segments.push(currentSegment);
        currentSegment = [points[i]];
      } else {
        currentSegment.push(points[i]);
      }
    }
  }
  if (currentSegment.length) segments.push(currentSegment);
  return segments;
}

// ----------------------------------------
// Ajusta la vista al contenido
// ----------------------------------------
const AjustarVista = ({ points = [], isTracking }) => {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    if (isTracking) {
      const last = points[points.length - 1];
      map.setView([last.lat, last.lng], 17);
    } else {
      const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
      map.fitBounds(bounds, { padding: [30, 30] });
    }
  }, [map, points, isTracking]);
  return null;
};

// ----------------------------------------
// Polyline clicable: busca el punto más cercano y abre Google Maps
// ----------------------------------------
const OpenablePolyline = ({ positions, segment }) => {
  const haversine = (lat1, lon1, lat2, lon2) => {
    const R = 6371e3;
    const toRad = (v) => (v * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const onClick = (e) => {
    const { lat, lng } = e.latlng;
    let minDist = Infinity;
    let nearest = positions[0];
    positions.forEach(([plat, plng]) => {
      const d = haversine(lat, lng, plat, plng);
      if (d < minDist) {
        minDist = d;
        nearest = [plat, plng];
      }
    });
    const [nLat, nLng] = nearest;
    const url = `https://www.google.com/maps/search/?api=1&query=${nLat},${nLng}`;
    window.open(url, "_blank");
  };

  const onMouseOver = (e) => {
    const { lat, lng } = e.latlng;
    let minDist = Infinity;
    let nearest;
    segment.forEach((p) => {
      const d = haversine(lat, lng, p.lat, p.lng);
      if (d < minDist) {
        minDist = d;
        nearest = p;
      }
    });
    const ts = new Date(nearest.timestamp).toLocaleString();
    e.target.unbindTooltip();
    e.target.bindTooltip(ts, { sticky: true }).openTooltip();
  };

  const onMouseOut = (e) => {
    e.target.closeTooltip();
  };

  const generateColor = (index, total) => {
    const ratio = index / total;
    const r = Math.floor(208 + ratio * (11 - 208));  // rojo a azul
    const g = Math.floor(44 + ratio * (18 - 44));
    const b = Math.floor(55 + ratio * (96 - 55));
    return `rgb(${r},${g},${b})`;
  };

  const lines = [];
  for (let i = 0; i < segment.length - 1; i++) {
    const p1 = segment[i];
    const p2 = segment[i + 1];
    const color = generateColor(i, segment.length - 1);

    lines.push(
      <Polyline
        key={i}
        positions={[
          [p1.lat, p1.lng],
          [p2.lat, p2.lng],
        ]}
        pathOptions={{ color }}
        eventHandlers={{
          click: onClick,
          mouseover: onMouseOver,
          mouseout: onMouseOut,
        }}
      />
    );
  }

  return <>{lines}</>;
};

// ----------------------------------------
// Componente Principal
// ----------------------------------------
const MapaRutaModal = ({ route, visible, onClose, username }) => {
  if (!visible || !route) return null;

  const points = Array.isArray(route.points) ? route.points : [];
  const stops = Array.isArray(route.stops) ? route.stops : [];
  const fuelLoads = Array.isArray(route.fuelLoads) ? route.fuelLoads : [];

  const iconMap = {
    routeStart: "https://cdn-icons-png.flaticon.com/128/14090/14090489.png",
    routeEnd: "https://cdn-icons-png.flaticon.com/128/854/854853.png",
    stop: "https://cdn-icons-png.flaticon.com/128/2698/2698545.png",
    fuel: "https://cdn-icons-png.flaticon.com/128/891/891035.png",
    current: "https://cdn-icons-png.flaticon.com/128/10161/10161680.png",
  };

  const createIcon = (url, size = [40, 40]) =>
    new L.Icon({
      iconUrl: url,
      iconSize: size,
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
    });

  const icons = {
    routeStart: createIcon(iconMap.routeStart, [25, 41]),
    routeEnd: createIcon(iconMap.routeEnd, [25, 41]),
    stop: createIcon(iconMap.stop),
    fuel: createIcon(iconMap.fuel),
    current: createIcon(iconMap.current),
  };

  const center = points.length ? [points[0].lat, points[0].lng] : [0, 0];
  const segments = points.length > 1 ? splitRouteByDistance(points) : [];

  const durationMs = !route.isTracking
    ? new Date(route.endTime) - new Date(route.startTime)
    : null;

  const getDurationString = (ms) => {
    const s = Math.floor(ms / 1000);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${h}h ${m}m ${sec}s`;
  };

  const distanceKm =
    route.finalKm != null && route.initialKm != null
      ? (route.finalKm - route.initialKm).toFixed(2)
      : null;

  return (
    <div
      id="modal-ruta"
      className="modal d-block"
      style={{ background: "#000000aa" }}
    >
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <strong>Usuario:</strong> {username}<br />
              <strong>Ruta:</strong> {route.routeId} <br />
              <strong>Inicio:</strong>{" "}
              {new Date(route.startTime).toLocaleString()} <br />
              <strong>Fin:</strong>{" "}
              {route.isTracking
                ? "En ruta"
                : new Date(route.endTime).toLocaleString()}
              <br />
              {!route.isTracking && (
                <>
                  <strong>Duración:</strong> {getDurationString(durationMs)}
                  <br />
                  {distanceKm && (
                    <>
                      <strong>Distancia recorrida:</strong> {distanceKm} km
                    </>
                  )}
                </>
              )}
            </h5>
            <Button text="X" color="red" fill action={onClose} classNames="close-button" />
          </div>
          <div className="modal-body" style={{ height: "500px" }}>
            <MapContainer
              center={center}
              zoom={15}
              scrollWheelZoom={false}
              style={{ height: "100%", width: "100%" }}
            >
              <TileLayer
                attribution="&copy; OpenStreetMap"
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <AjustarVista points={points} isTracking={route.isTracking} />
              {segments.map((seg, i) =>
                seg.length > 1 ? (
                  <OpenablePolyline
                    key={i}
                    positions={seg.map((p) => [p.lat, p.lng])}
                    segment={seg}              // ← aquí
                  />
                ) : null
              )}

              {points[0] && (
                <Marker
                  icon={icons.routeStart}
                  position={[points[0].lat, points[0].lng]}
                >
                  <Popup>
                    Inicio de ruta
                    <br />
                    Km inicial: <strong>{route.initialKm ?? "—"}</strong>
                    <br />
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${points[0].lat},${points[0].lng}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Ver en Google Maps
                    </a>
                  </Popup>
                </Marker>
              )}
              {!route.isTracking && points.length > 1 && (
                <Marker
                  icon={icons.routeEnd}
                  position={[
                    points[points.length - 1].lat,
                    points[points.length - 1].lng,
                  ]}
                >
                  <Popup>
                    Fin de ruta
                    <br />
                    Km final: <strong>{route.finalKm ?? "—"}</strong>
                    <br />
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${points[
                        points.length - 1
                      ].lat},${points[points.length - 1].lng}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Ver en Google Maps
                    </a>
                  </Popup>
                </Marker>
              )}
              {route.isTracking && points.length > 0 && (
                <Marker
                  icon={icons.current}
                  position={[
                    points[points.length - 1].lat,
                    points[points.length - 1].lng,
                  ]}
                >
                  <Popup>
                    Ubicación actual de la ruta
                  </Popup>
                </Marker>
              )}
              {stops.map((stop, j) =>
                stop.startLat != null && stop.endLat != null ? (
                  <React.Fragment key={j}>
                    <Marker
                      icon={icons.stop}
                      position={[stop.startLat, stop.startLng]}
                    >
                      <Popup>
                        P:{j + 1} - {stop.name} – Inicio
                        <br />
                        Km en parada: <strong>{stop.kmAtStop ?? "—"}</strong>
                        <br />
                        Duración parada:{" "}
                        <strong>
                          {getDurationString(
                            stop.paradaFinishTimeStamp - stop.paradaInitTimeStamp
                          )}
                        </strong>
                        <br />
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${stop.startLat},${stop.startLng}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Ver en Google Maps
                        </a>
                      </Popup>
                    </Marker>
                    <Marker
                      icon={icons.stop}
                      position={[stop.endLat, stop.endLng]}
                    >
                      <Popup>
                        P:{j + 1} - {stop.name} – Fin
                        <br />
                        Duración parada:{" "}
                        <strong>
                          {getDurationString(
                            stop.paradaFinishTimeStamp - stop.paradaInitTimeStamp
                          )}
                        </strong>
                        <br />
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${stop.endLat},${stop.endLng}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Ver en Google Maps
                        </a>
                      </Popup>
                    </Marker>
                  </React.Fragment>
                ) : null
              )}
              {fuelLoads.map((fuel, k) =>
                fuel.startLat != null && fuel.endLat != null ? (
                  <React.Fragment key={k}>
                    <Marker
                      icon={icons.fuel}
                      position={[fuel.startLat, fuel.startLng]}
                    >
                      <Popup>
                        Gasolina – Inicio
                        <br />
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${fuel.startLat},${fuel.startLng}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Ver en Google Maps
                        </a>
                      </Popup>
                    </Marker>
                    <Marker
                      icon={icons.fuel}
                      position={[fuel.endLat, fuel.endLng]}
                    >
                      <Popup>
                        Gasolina – Fin
                        <br />
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${fuel.endLat},${fuel.endLng}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Ver en Google Maps
                        </a>
                      </Popup>
                    </Marker>
                  </React.Fragment>
                ) : null
              )}
            </MapContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapaRutaModal;
